import React, { useState, FormEvent, ChangeEvent, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Button, Alert, Image } from 'react-bootstrap';
import axios from '../../../api/axios';
import { validateEmail, containsEmoji } from '../../../utils/Utils';
import { PASSWORD_VALIDATION } from '../../../utils/Constants';
import { ApiError } from '../../../types/errors';
import './AdminLogin.css';

interface AdminLoginErrors {
  email?: string;
  password?: string;
  auth?: string;
}

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errors, setErrors] = useState<AdminLoginErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      navigate('/admin/clients');
    }
  }, [navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const newErrors: AdminLoginErrors = {};
    if (!email) newErrors.email = 'メールアドレスを入力してください。';
    else if (!validateEmail(email)) newErrors.email = '不正なメールアドレスです。';
    else if (containsEmoji(email)) newErrors.email = '絵文字は使用できません。';

    if (!password) newErrors.password = 'パスワードを入力してください。';
    else if (containsEmoji(password)) newErrors.password = '絵文字は使用できません。';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await axios.post('/admin/login', { email, password });
      localStorage.setItem('admin_token', response.data.token);
      localStorage.setItem('admin', JSON.stringify(response.data.admin));

      navigate('/admin/clients');
    } catch (error: unknown) {
      console.error('Login error:', error);
      const axiosError = error as ApiError;
      if (axiosError.response) {
        if (axiosError.response.status === 401) {
          setErrors({ auth: 'メールアドレスまたはパスワードが正しくありません。' });
        } else if (axiosError.response.status === 500) {
          setErrors({ auth: 'データベース接続エラーが発生しました。しばらく経ってから再度お試しください。' });
        } else {
          setErrors({ auth: 'ログイン処理中にエラーが発生しました。' });
        }
      } else if (axiosError.request) {
        setErrors({ auth: 'サーバーに接続できません。ネットワーク接続を確認してください。' });
      } else {
        setErrors({ auth: 'ログイン処理中にエラーが発生しました。' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="blank-container">
      <div className="blank-content">
        <div className="login-form-container">
          <div className='login-form-background'></div>
          <div className="text-center mb-4">
            <Image 
              src="/logo.svg" 
              alt="ESF Logo" 
              className="logo-image" 
            />
            <h4>アドミン</h4>
          </div>

          {errors.auth && <Alert variant="danger">{errors.auth}</Alert>}

          <Form onSubmit={handleSubmit} className="login-form">
            <Form.Group className="mb-4">
              <Form.Control
                type="text"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                isInvalid={!!errors.email}
                placeholder="メールアドレス"
                className="login-input"
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
              <div className="d-flex justify-content-between mt-2">
                <small className="text-muted">例: xxxxxxxxx@xxxxx.com</small>
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Control
                type="password"
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                isInvalid={!!errors.password}
                placeholder="パスワード"
                className="login-input"
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {errors.password}
              </Form.Control.Feedback>
              <div className="d-flex justify-content-between mt-2">
                <small className="text-muted">{PASSWORD_VALIDATION.SHORT_COMMENT}</small>
                <Link to="/admin/forgot-password" className="forgot-password-link">
                  パスワードを忘れた方
                </Link>
              </div>
            </Form.Group>

            <div className="login-button-container">
              <Button 
                variant="success" 
                type="submit" 
                className="login-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'ログイン中...' : 'ログイン'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
